import {
  XIcon,
  ArrowLeftIcon,
  CameraIcon,
  ListStarIcon,
  WarningIcon,
  BellIcon,
  PlusCircleIcon,
} from "@phosphor-icons/react";
import { useI18N } from "common/hooks";
import { cn } from "@/lib/utils";

export type ContributionStep = "intro" | "options" | "unsupported";
export type ContributionVariant = "card" | "sheet" | "page";
export type ContributionAction = "photos" | "experiences" | "alerts";

export const CONTRIBUTION_OPTIONS = [
  { id: "photos", labelKey: "poiAddPhotos", Icon: CameraIcon },
  { id: "experiences", labelKey: "poiShareExperiences", Icon: ListStarIcon },
  { id: "alerts", labelKey: "poiReportAlert", Icon: WarningIcon },
] as const;

type Props = {
  step: ContributionStep;
  /** True while the grey venue is being created before navigation. */
  creating: boolean;
  /** Venue/place name, when known. Omitted for unsupported places. */
  name?: string;
  /** card = desktop anchored popover, sheet = mobile bottom sheet, page = mobile full-screen */
  variant?: ContributionVariant;
  onGetNotified: () => void;
  onShowOptions: () => void;
  onSelectAction: (action: ContributionAction) => void;
  onClose: () => void;
  onBack?: () => void;
};

type Option = (typeof CONTRIBUTION_OPTIONS)[number];

/**
 * Opt-in contribution flow shown when a native Google POI (or grey venue) is
 * tapped. intro (Get notified / Add information) -> options (pick an action)
 * -> navigate to the venue detail page. "unsupported" is terminal.
 */
export function PoiContributionCard({
  step,
  creating,
  name,
  variant = "card",
  onGetNotified,
  onShowOptions,
  onSelectAction,
  onClose,
  onBack,
}: Props) {
  const i18n = useI18N();

  const OptionTile = ({
    option: { id, labelKey, Icon },
    large,
  }: {
    option: Option;
    large: boolean;
  }) => (
    <button
      type="button"
      disabled={creating}
      onClick={(e) => {
        e.stopPropagation();
        onSelectAction(id);
      }}
      className={cn(
        "flex flex-col items-start gap-2 rounded-xl border text-left transition-colors focus-blue",
        "border-border-token-neutral-minimal hover:bg-bg-brand-ghost-hover",
        large ? "p-4 min-h-[120px]" : "p-3",
        creating && "opacity-40 cursor-not-allowed",
      )}
    >
      <span
        className={cn(
          "flex items-center justify-center rounded-md bg-bg-brand-ghost-hover",
          large ? "w-10 h-10" : "w-8 h-8",
        )}
      >
        <Icon size={large ? 22 : 18} className="text-fg-brand-subtle" />
      </span>
      <span className={large ? "title-s-semibold" : "label-m-medium"}>
        {i18n.get(labelKey)}
      </span>
    </button>
  );

  // Mobile full-screen "What you can do" page.
  if (variant === "page") {
    return (
      <div className="flex flex-col h-full w-full bg-bg-brand-secondary-default text-fg-neutral-strong">
        <div className="flex items-center px-4 pt-4 pb-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              (onBack ?? onClose)();
            }}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-bg-brand-ghost-hover focus-blue"
            aria-label={i18n.get("back")}
          >
            <ArrowLeftIcon size={22} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4">
          <h1 className="title-l-bold mb-1">{i18n.get("poiWhatYouCanDo")}</h1>
          <p className="body-m-medium text-fg-neutral-medium mb-5">
            {i18n.get("poiSelectApply")}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {CONTRIBUTION_OPTIONS.map((o) => (
              <OptionTile key={o.id} option={o} large />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const isSheet = variant === "sheet";

  return (
    <div
      className={cn(
        "relative bg-bg-brand-secondary-default text-fg-neutral-strong",
        isSheet
          ? "rounded-2xl p-5 w-full shadow-xl"
          : step === "options"
            ? "rounded-2xl p-6 w-[480px] max-w-[92vw] shadow-xl"
            : "rounded-xl p-4 w-[320px] max-w-[80vw]",
      )}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center rounded-full bg-bg-brand-secondary-default border border-border-token-neutral-minimal shadow-md hover:bg-bg-brand-ghost-hover focus-blue"
        aria-label={i18n.get("close")}
        type="button"
      >
        <XIcon size={18} weight="bold" />
      </button>

      {step === "intro" && (
        <div>
          {name && (
            <p className="title-s-semibold text-fg-neutral-strong mb-0.5 truncate">
              {name}
            </p>
          )}
          <h3 className="label-m-semibold text-fg-neutral-strong mb-1">
            {i18n.get("poiNotHereTitle")}
          </h3>
          <p className="body-m-medium text-fg-neutral-medium mb-4">
            {i18n.get("poiNotHereSubtitle")}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onGetNotified();
              }}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg py-3 label-m-semibold border border-border-token-neutral-minimal text-fg-neutral-strong hover:bg-bg-brand-ghost-hover focus-blue"
            >
              <BellIcon size={18} />
              {i18n.get("getAlerts")}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onShowOptions();
              }}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg py-3 label-m-semibold bg-bg-brand-primary-default text-white hover:bg-bg-brand-primary-hover focus-blue"
            >
              <PlusCircleIcon size={18} weight="fill" />
              {i18n.get("addInfo")}
            </button>
          </div>
        </div>
      )}

      {step === "options" && (
        <div>
          <h3 className="title-l-bold mb-1 pr-8">
            {i18n.get("poiWhatYouCanDo")}
          </h3>
          <p className="body-m-medium text-fg-neutral-medium mb-5">
            {i18n.get("poiSelectApply")}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {CONTRIBUTION_OPTIONS.map((o) => (
              <OptionTile key={o.id} option={o} large />
            ))}
          </div>
        </div>
      )}

      {step === "unsupported" && (
        <div>
          <h3 className="title-s-semibold text-fg-neutral-strong mb-1">
            {i18n.get("poiUnsupportedCardTitle")}
          </h3>
          <p className="body-m-medium text-fg-neutral-medium">
            {i18n.get("poiUnsupportedSubtitle")}
          </p>
        </div>
      )}
    </div>
  );
}
